import { useLocation, useMatch, useNavigate } from 'react-router';

import { PATHS } from '@/shared/config';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { debug } from '@/shared/lib';

import { getRecordingPath, transcriptorModel } from '@/entities/transcriptor';

import {
  audioRecordingService,
  useLiveSession,
} from '@/features/audioRecording';

const { startUploading } = transcriptorModel.actions;
const { selectConsultationId, selectPatientId } = transcriptorModel.selectors;

// The recording's page, unless it is open already: opening it again would add
// a history entry that Back returns to the same page.
export const useOpenRecordingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  return (patientID: string, recordingID: string) => {
    const path = getRecordingPath({ patientID, recordingID });
    if (`${location.pathname}${location.search}` !== path) {
      navigate(path);
    }
  };
};

// Finish, from the corner window or the recording page. The clinician goes on
// to the recording's page, where its processing is followed, for a recording
// started from its start page or stopped on the patient's recordings.
export const useRecordingStop = () => {
  const dispatch = useAppDispatch();
  const openRecordingPage = useOpenRecordingPage();
  const recordingsPatientId = useMatch(PATHS.patientRecordings)?.params
    .patientID;
  const consultationId = useAppSelector(selectConsultationId);
  const patientId = useAppSelector(selectPatientId);
  const session = useLiveSession();
  const ownSession =
    !!consultationId && session?.meta.encounterId === consultationId;

  return async () => {
    dispatch(startUploading());
    if (
      patientId &&
      consultationId &&
      (ownSession || recordingsPatientId === patientId)
    ) {
      openRecordingPage(patientId, consultationId);
    }
    try {
      await audioRecordingService.stopRecording('user');
      // stopRecording action is dispatched by the onStopped callback in useStartRecording
    } catch (error) {
      debug(error);
    }
  };
};
